/** @jsxImportSource @emotion/react */

import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "components/Button";
import { Modal } from "components/Modal";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useState,
} from "react";
import { AuthService } from "services/Auth";
import UserService, {
  invalidOtpString,
  localStorageUserName,
  otpRequiredString,
} from "services/User";
import { User } from "services/User.d";
import tw from "twin.macro";

export interface UserState {
  user: User | null;
  setUser: Dispatch<SetStateAction<User | null>>;
  resetUser: () => void;
  isLoggedIn: boolean;
  setIsLoggedIn: Dispatch<SetStateAction<boolean>>;
  userOtp: string;
  setUserOtp: Dispatch<SetStateAction<string>>;
  readonly signout: () => Promise<void>;
  setIsOpen: Dispatch<SetStateAction<boolean | undefined>>;
  windowWidth: number;
  windowHeight: number;
  setWindowWidth: Dispatch<SetStateAction<number>>;
  setWindowHeight: Dispatch<SetStateAction<number>>;
  hideNav: boolean;
  setHideNav: Dispatch<SetStateAction<boolean>>;
  discrepancyCount: number;
  setDiscrepancyCount: Dispatch<SetStateAction<number>>;
}

const defaultState = {
  user: null,
  setUser: () => {},
  resetUser: () => {},
  isLoggedIn: false,
  setIsLoggedIn: () => {},
  userOtp: "",
  setUserOtp: () => {},
  logout: () => {},
  signout: async () => {},
  setIsOpen: () => {},
  windowWidth: 0,
  windowHeight: 0,
  setWindowWidth: () => {},
  setWindowHeight: () => {},
  hideNav: false,
  setHideNav: () => {},
  discrepancyCount: 0,
  setDiscrepancyCount: () => {},
} as UserState;

export const UserContext = createContext<UserState>(defaultState);
const authService = new AuthService();
const userService = new UserService();

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [hideNav, setHideNav] = useState<boolean>(false);

  const [user, setUser] = useState(userService.getUser() || null);
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const [userOtp, setUserOtp] = useState<string>("");

  const [discrepancyCount, setDiscrepancyCount] = useState<number>(0);

  const [windowWidth, setWindowWidth] = useState<number>(
    window.innerWidth || 0
  );
  const [windowHeight, setWindowHeight] = useState<number>(
    window.innerHeight || 0
  );

  const getUser = async () => {
    let currentUser = authService.getCurrentUser();
    const start = Date.now();
    while (!currentUser && Date.now() - start < 3000) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      currentUser = authService.getCurrentUser();
      setUser(userService.getUser());
    }
    if (currentUser === null) {
      console.log(
        "No Keycloak user found after trying for 3000ms, logging out."
      );
      signout();
    } else {
      if (currentUser) {
        console.log("User successfully logged in with Keycloak");
        setIsLoggedIn(true);
        const token = currentUser.split(",");
        await authService.loadUser(token.toString(), userOtp).then((res) => {
          if (typeof res === "string" && res === otpRequiredString) {
            userService.setUser({
              otpRequired: true,
            } as unknown as User);
            // if page is not home page, redirect to home page
            if (window.location.pathname !== "/home") {
              window.location.replace("/home");
            }
          }
          if (typeof res === "string" && res === invalidOtpString) {
            userService.setUser({
              invalidOtp: true,
            } as unknown as User);
          }
          if (res !== null && typeof res !== "string") setUser(res);
          if (res === null) signout();
        });
      }
      setIsLoggedIn(true);
    }
  };

  const signout = async () => {
    try {
      await authService.logout();

      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem(localStorageUserName);

      if (process.env.REACT_APP_USER_KEY) {
        sessionStorage.removeItem(process.env.REACT_APP_USER_KEY);
      }
      if (process.env.REACT_APP_REFRESH_KEY) {
        sessionStorage.removeItem(process.env.REACT_APP_REFRESH_KEY);
      }

      window.location.replace("/login");
    } catch (logoutError) {
      console.error("Logout failed:", logoutError);
    }
  };

  useEffect(() => {
    if (window.innerWidth <= 1100) {
      setHideNav(true);
    }
    getUser();
  }, [userOtp]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth || 0);
      setWindowHeight(window.innerHeight || 0);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const value = {
    user,
    setUser,
    resetUser: userService.resetUser,
    isLoggedIn,
    setIsLoggedIn,
    userOtp,
    setUserOtp,
    signout,
    isOpen,
    setIsOpen,
    windowWidth,
    windowHeight,
    setWindowWidth,
    setWindowHeight,
    hideNav,
    setHideNav,
    discrepancyCount,
    setDiscrepancyCount,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
      <Modal
        width="s"
        disableCloseButton={true}
        outsideCloseDisabled={true}
        isOpen={isOpen}
        title="Session Expired"
        isFullScreen={undefined}
        kind="danger"
        onClose={() => setIsOpen(undefined)}
        secondary={
          <Button variant="outline" onClick={signout}>
            Login
          </Button>
        }
        content={
          <div css={[tw`text-center space-y-6`]}>
            <ExclamationCircleIcon css={[tw`text-red-500 w-16 m-auto`]} />
            <div>
              <p>Your session has expired.</p> <p> Please login again.</p>
            </div>
          </div>
        }
      />
    </UserContext.Provider>
  );
};
